// @flow
// $FlowFixMe - react-query types not available
import { useInfiniteQuery, useQuery, useQueryClient } from 'react-query';
import { useCallback } from 'react';
import { toastr } from 'react-redux-toastr';
import useIntl from '../../hooks/useIntl';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import { fetchPropertyLeslieStatus } from '../AIAssistants/data';
import { fetchMessagingHub } from './data';
import intlMessages from './messages';
import type { UseMessagingHubParams, UseMessagingHubReturn } from './types';

/**
 * Whether the Message Status filter offers Leslie Needs Help.
 *
 * Across the portfolio it always does. For a single property it only does once that
 * property's Leslie status has come back on; a failed or forbidden read leaves it hidden.
 */
export function useIsNeedsHelpFilterShown({
  isPropertyLevel,
}: {
  isPropertyLevel: boolean,
}): boolean {
  const selectedProperty = useSelectedProperty();

  const { data: leslieStatus } = useQuery(
    [
      'propertyLeslieStatus',
      selectedProperty?.organizationId,
      selectedProperty?.id,
    ],
    () => fetchPropertyLeslieStatus({ selectedProperty }),
    {
      enabled: Boolean(isPropertyLevel && selectedProperty?.id),
      retry: false,
    },
  );

  if (!isPropertyLevel) return true;
  return leslieStatus?.enabled === true;
}

export function useMessagingHub({
  search,
  statuses,
  propertyIds,
  householdStatuses,
  forcePropertyScope,
}: UseMessagingHubParams): UseMessagingHubReturn {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const queryKey = [
    'messagingHub',
    { search, statuses, propertyIds, householdStatuses, forcePropertyScope },
  ];

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      fetchMessagingHub({
        limit: 20,
        cursor: pageParam,
        search,
        statuses,
        propertyIds,
        householdStatuses,
        forcePropertyScope,
      }),
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.hasMore
        ? lastPage.pagination.nextCursor
        : undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // refetchInterval: 60000, // Refresh every 60 seconds
    onError: () => {
      toastr.error(
        intl.formatMessage(intlMessages.errorHeader),
        intl.formatMessage(intlMessages.failedToLoadMessagesError),
      );
    },
  });

  const conversations = data?.pages.flatMap((page) => page.conversations) || [];

  const filteredConversations = conversations.filter((conversation) => {
    if (!propertyIds || propertyIds.length === 0) {
      return !conversation.recipient?.property?.isTestProperty;
    }

    return true;
  });

  const updateConversationLocally = useCallback(
    (conversationId, updates) => {
      queryClient.setQueriesData(['messagingHub'], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            conversations: page.conversations.map((conv) =>
              conv.conversationId === conversationId
                ? { ...conv, ...updates }
                : conv,
            ),
          })),
        };
      });
    },
    [queryClient],
  );

  return {
    conversations: filteredConversations,
    isLoading,
    isLoadingMore: isFetchingNextPage,
    loadMore: fetchNextPage,
    hasMore: hasNextPage,
    refresh: refetch,
    updateConversationLocally,
  };
}
